import {Filter} from '@github-ui/filter'
import type {FilterProps} from '@github-ui/filter'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This subpath is Relay-free; the package retains Relay only for legacy callers.
import {RepoScopedCreateIssueButton} from '@github-ui/issue-create/RepoScopedCreateIssueButton'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useParams} from '@github-ui/react-router'
import {repoSidebarLinks} from '@github-ui/repos-issues-sidebar-ui/repos-issues-sidebar-links'
import {useIssuesSidebarState} from '@github-ui/repos-issues-sidebar-ui/use-issues-sidebar-state'
import {SharedHeader} from '@github-ui/shared-dashboard-surface/components/shared/SharedHeader'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {Button} from '@primer/react'
import {type ReactNode, type RefObject, useEffect, useId, useState} from 'react'

import {useIssuesIndexResults} from '../hooks/use-issues-index-results'
import {issuesLayoutRoute} from './issues-layout-route'
import styles from './IssuesIndexSearch.module.css'

const EMPTY_SAVED_VIEWS: never[] = []

/**
 * Repo Issues#index search input: a repo-scoped `<Filter>` (autocomplete scoped to
 * the current repo) that navigates the index route with the new `q` on submit.
 */
export function IssuesIndexSearch({
  inputRef,
  beforeFilter,
}: {
  inputRef?: RefObject<HTMLInputElement | null>
  beforeFilter?: ReactNode
}) {
  const {owner, repo} = useParams<{owner: string; repo: string}>()
  const {fetchResults, fetchedQuery, getQueryHref} = useIssuesIndexResults()
  const {selectedLink: selectedLinkId} = useIssuesSidebarState()

  const [query, setQuery] = useState(fetchedQuery)

  // Re-sync the input when the URL query changes (navigation, sort/state, pagination).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuery(fetchedQuery)
  }, [fetchedQuery])

  const providers = useFilterProviders({
    repositoryScope: owner && repo ? `${owner}/${repo}` : undefined,
    isIssueSearchScope: true,
  })

  const filterProps: FilterProps = {
    id: 'issues-index-filter',
    label: 'Search issues',
    variant: 'input',
    placeholder: 'Search issues',
    filterValue: query,
    providers,
    onChange: setQuery,
    onSubmit: filterQuery => fetchResults({q: filterQuery.raw}),
    settings: {aliasMatching: true, groupAndKeywordSupport: true},
  }

  const links = repoSidebarLinks.map(link => ({...link, path: getQueryHref(link.query)}))
  const selectedLink = links.find(link => link.id === selectedLinkId)

  return (
    <SharedHeader
      dashboardType="issues"
      links={links}
      selectedLink={selectedLink}
      onSelectLink={link => fetchResults({q: link.query})}
      savedViews={EMPTY_SAVED_VIEWS}
      showSavedViews={false}
      showOptionsButton={false}
      optOutLocked
      generateLinkPath={(path, filterQuery) => path ?? getQueryHref(filterQuery ?? '')}
      filter={
        <div className={styles.FilterContainer}>
          {beforeFilter}
          <div role="search" aria-label="Issues">
            <Filter {...filterProps} inputRef={inputRef} data-testid="issues-index-filter-component" />
          </div>
        </div>
      }
      primaryAction={<RepoIssuesPrimaryAction />}
    />
  )
}

function RepoIssuesPrimaryAction() {
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const {owner, repo} = useParams<{owner: string; repo: string}>()
  const action = layoutPayload?.actions?.newIssue
  const disabledReasonId = useId()

  if (action?.url && owner && repo) {
    return (
      <RepoScopedCreateIssueButton
        owner={owner}
        repo={repo}
        label={action.label}
        emojiSkinTonePreference={layoutPayload?.currentUserSettings?.preferredEmojiSkinTone}
      />
    )
  }
  if (!action?.disabledReason) return null

  return (
    <>
      <Button variant="primary" inactive aria-disabled="true" aria-describedby={disabledReasonId}>
        {action.label}
      </Button>
      <span id={disabledReasonId} className="color-fg-muted text-small">
        {action.disabledReason}
      </span>
    </>
  )
}
